import React, { useEffect, useRef } from 'react';

const QUILL_CSS_SELECTOR = 'link[data-onboarding-quill="css"]';
const QUILL_JS_SELECTOR = 'script[data-onboarding-quill="js"]';
const QUILL_CSS_URL = 'https://cdn.quilljs.com/1.3.6/quill.snow.css';
const QUILL_JS_URL = 'https://cdn.quilljs.com/1.3.6/quill.js';

const ensureQuillLoaded = () => {
    return new Promise((resolve, reject) => {
        if (window.Quill) {
            resolve();
            return;
        }

        let quillCSS = document.querySelector(QUILL_CSS_SELECTOR);
        if (!quillCSS) {
            quillCSS = document.createElement('link');
            quillCSS.rel = 'stylesheet';
            quillCSS.href = QUILL_CSS_URL;
            quillCSS.setAttribute('data-onboarding-quill', 'css');
            document.head.appendChild(quillCSS);
        }

        let quillScript = document.querySelector(QUILL_JS_SELECTOR);
        if (!quillScript) {
            quillScript = document.createElement('script');
            quillScript.src = QUILL_JS_URL;
            quillScript.async = true;
            quillScript.setAttribute('data-onboarding-quill', 'js');
            document.body.appendChild(quillScript);
        }

        const onLoad = () => resolve();
        const onError = () => reject(new Error('Failed to load Quill'));

        quillScript.addEventListener('load', onLoad, { once: true });
        quillScript.addEventListener('error', onError, { once: true });

        if (quillScript.getAttribute('data-loaded') === 'true') {
            resolve();
            return;
        }

        quillScript.addEventListener('load', () => {
            quillScript.setAttribute('data-loaded', 'true');
        }, { once: true });
    });
};

const OnboardingQuillEditor = ({
    value = '',
    onChange,
    placeholder = '',
    className = ''
}) => {
    const editorRef = useRef(null);
    const quillRef = useRef(null);
    const lastHtmlRef = useRef(value || '');

    useEffect(() => {
        let mounted = true;

        const init = async () => {
            try {
                await ensureQuillLoaded();
                if (!mounted || !editorRef.current || quillRef.current) return;

                const quill = new window.Quill(editorRef.current, {
                    theme: 'snow',
                    placeholder,
                    modules: {
                        toolbar: [
                            [{ header: [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ list: 'ordered' }, { list: 'bullet' }],
                            ['link'],
                            ['clean']
                        ]
                    }
                });

                quillRef.current = quill;
                quill.clipboard.dangerouslyPasteHTML(value || '');
                lastHtmlRef.current = value || '';

                quill.on('text-change', () => {
                    const html = quill.root.innerHTML || '';
                    lastHtmlRef.current = html;
                    onChange?.(html);
                });
            } catch (error) {
                console.error('Failed to initialize onboarding quill:', error);
            }
        };

        init();

        return () => {
            mounted = false;
            quillRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const quill = quillRef.current;
        if (!quill) return;
        const nextValue = value || '';
        if (nextValue === lastHtmlRef.current) return;
        quill.clipboard.dangerouslyPasteHTML(nextValue);
        lastHtmlRef.current = nextValue;
    }, [value]);

    return (
        <div className={`onboarding-quill ${className}`}>
            <div ref={editorRef} />
        </div>
    );
};

export default OnboardingQuillEditor;
